import { useState, useEffect, useCallback } from "react";
import "./AdminSalaryGuide.css";

/* ─────────────────────────────────────────
   CONFIG
───────────────────────────────────────── */
const API_ROOT = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
const API_BASE = API_ROOT ? `${API_ROOT.replace(/\/api$/, "")}/api` : "/api";
const ADMIN_TOKEN = process.env.REACT_APP_ADMIN_TOKEN || "your-secret-admin-token";

const adminHeaders = {
  "Content-Type": "application/json",
  "x-admin-token": ADMIN_TOKEN,
};

function formatLPA(val) {
  if (val === undefined || val === null || val === "") return "—";
  return `₹${parseFloat(val).toFixed(1)} LPA`;
}

const CATEGORIES = ["Technology","Data","AI/ML","Product","Design","Finance","HR","Operations"];
const ICONS = ["💼","💻","📊","🤖","📦","🎨","🏦","👥","🔧","☁️","🛡️","📱","📈","⚡","🌐"];

/* ─────────────────────────────────────────
   EDIT MODAL
───────────────────────────────────────── */
function EditModal({ entry, onSave, onClose }) {
  const [form, setForm] = useState({
    icon:           entry.icon || "💼",
    category:       entry.category || "Technology",
    description:    entry.description || "",
    adminOverride:  entry.adminOverride || false,
    overrideAvgLPA: entry.overrideAvgLPA ?? entry.avgSalaryLPA ?? "",
    overrideMinLPA: entry.overrideMinLPA ?? entry.minSalaryLPA ?? "",
    overrideMaxLPA: entry.overrideMaxLPA ?? entry.maxSalaryLPA ?? "",
    overrideNote:   entry.overrideNote || "",
    // salaryByExp
    expFresher: entry.salaryByExp?.fresher ?? "",
    expJunior:  entry.salaryByExp?.junior  ?? "",
    expMid:     entry.salaryByExp?.mid     ?? "",
    expSenior:  entry.salaryByExp?.senior  ?? "",
    expLead:    entry.salaryByExp?.lead    ?? "",
    // salaryByCity
    cityBangalore: entry.salaryByCity?.find(c => c.city==="Bangalore")?.avgLPA ?? "",
    cityMumbai:    entry.salaryByCity?.find(c => c.city==="Mumbai")?.avgLPA    ?? "",
    cityDelhi:     entry.salaryByCity?.find(c => c.city==="Delhi")?.avgLPA     ?? "",
    cityHyderabad: entry.salaryByCity?.find(c => c.city==="Hyderabad")?.avgLPA ?? "",
    cityPune:      entry.salaryByCity?.find(c => c.city==="Pune")?.avgLPA      ?? "",
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr]       = useState("");

  const set = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  const handleSave = async () => {
    setSaving(true);
    setErr("");
    try {
      const payload = {
        icon:          form.icon,
        category:      form.category,
        description:   form.description,
        adminOverride: form.adminOverride,
        overrideNote:  form.overrideNote,
        updatedBy:     "admin",
      };
      if (form.adminOverride) {
        payload.overrideAvgLPA = parseFloat(form.overrideAvgLPA) || 0;
        payload.overrideMinLPA = parseFloat(form.overrideMinLPA) || 0;
        payload.overrideMaxLPA = parseFloat(form.overrideMaxLPA) || 0;
      }
      payload.salaryByExp = {
        fresher: parseFloat(form.expFresher) || null,
        junior:  parseFloat(form.expJunior)  || null,
        mid:     parseFloat(form.expMid)     || null,
        senior:  parseFloat(form.expSenior)  || null,
        lead:    parseFloat(form.expLead)    || null,
      };
      payload.salaryByCity = [
        { city:"Bangalore", avgLPA: parseFloat(form.cityBangalore)||0 },
        { city:"Mumbai",    avgLPA: parseFloat(form.cityMumbai)   ||0 },
        { city:"Delhi",     avgLPA: parseFloat(form.cityDelhi)    ||0 },
        { city:"Hyderabad", avgLPA: parseFloat(form.cityHyderabad)||0 },
        { city:"Pune",      avgLPA: parseFloat(form.cityPune)     ||0 },
      ].filter(c => c.avgLPA > 0);

      const res  = await fetch(`${API_BASE}/salary-guide/admin/${entry._id}`, {
        method: "PUT", headers: adminHeaders, body: JSON.stringify(payload),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error);
      onSave(json.data);
    } catch (e) {
      setErr(e.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="asg-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="asg-edit-modal">
        <div className="asg-edit-modal__header">
          <div>
            <div className="asg-edit-modal__title">Edit Salary Guide — {entry.role}</div>
            <div className="asg-edit-modal__sub">Role slug: <code>{entry.roleSlug}</code></div>
          </div>
          <button className="asg-icon-btn" onClick={onClose}>✕</button>
        </div>

        <div className="asg-edit-modal__body">

          {/* ─ Metadata ─ */}
          <div className="asg-section">
            <div className="asg-section__title">📋 Metadata</div>
            <div className="asg-form-row">
              <div className="asg-field">
                <label>Icon</label>
                <div className="asg-icon-picker">
                  {ICONS.map(ic => (
                    <button key={ic}
                      className={`asg-icon-opt ${form.icon === ic ? "asg-icon-opt--active" : ""}`}
                      onClick={() => set("icon", ic)}>{ic}</button>
                  ))}
                </div>
              </div>
              <div className="asg-field">
                <label>Category</label>
                <select className="asg-select" value={form.category} onChange={e => set("category", e.target.value)}>
                  {CATEGORIES.map(c => <option key={c}>{c}</option>)}
                </select>
              </div>
            </div>
            <div className="asg-field">
              <label>Description</label>
              <textarea className="asg-textarea" rows={2}
                value={form.description} onChange={e => set("description", e.target.value)}
                placeholder="Short description of this role…" />
            </div>
          </div>

          {/* ─ Calculated (read-only) ─ */}
          <div className="asg-section">
            <div className="asg-section__title">📊 Auto-Calculated (from job postings)</div>
            <div className="asg-calc-row">
              <div className="asg-calc-box">
                <div className="asg-calc-box__lbl">Avg Salary</div>
                <div className="asg-calc-box__val">{formatLPA(entry.avgSalaryLPA)}</div>
              </div>
              <div className="asg-calc-box">
                <div className="asg-calc-box__lbl">Min</div>
                <div className="asg-calc-box__val">{formatLPA(entry.minSalaryLPA)}</div>
              </div>
              <div className="asg-calc-box">
                <div className="asg-calc-box__lbl">Max</div>
                <div className="asg-calc-box__val">{formatLPA(entry.maxSalaryLPA)}</div>
              </div>
              <div className="asg-calc-box">
                <div className="asg-calc-box__lbl">Job Count</div>
                <div className="asg-calc-box__val">{entry.jobCount || "—"}</div>
              </div>
            </div>
            <div style={{ fontSize:".73rem", color:"var(--asg-ink4)", marginTop:6 }}>
              Last calculated: {entry.lastCalculatedAt ? new Date(entry.lastCalculatedAt).toLocaleString() : "Never"}
            </div>
          </div>

          {/* ─ Admin Override Toggle ─ */}
          <div className="asg-section">
            <div className="asg-section__title">
              ✏️ Admin Override
              <label className="asg-toggle" style={{ marginLeft:"auto" }}>
                <input type="checkbox" checked={form.adminOverride}
                  onChange={e => set("adminOverride", e.target.checked)} />
                <span className="asg-toggle__slider" />
              </label>
            </div>
            <div style={{ fontSize:".8rem", color:"var(--asg-ink3)", marginBottom: form.adminOverride ? 14 : 0 }}>
              {form.adminOverride
                ? "Override is ON — your values below will be shown to users instead of auto-calculated."
                : "Override is OFF — calculated values from job postings are shown to users."}
            </div>

            {form.adminOverride && (
              <>
                <div className="asg-form-row asg-form-row--3">
                  <div className="asg-field">
                    <label>Override Min LPA</label>
                    <div className="asg-input-wrap">
                      <span>₹</span>
                      <input type="number" className="asg-input" step="0.1" min="0"
                        value={form.overrideMinLPA} onChange={e => set("overrideMinLPA", e.target.value)}
                        placeholder="e.g. 8.0" />
                    </div>
                  </div>
                  <div className="asg-field">
                    <label>Override Avg LPA <span className="asg-required">*</span></label>
                    <div className="asg-input-wrap">
                      <span>₹</span>
                      <input type="number" className="asg-input" step="0.1" min="0"
                        value={form.overrideAvgLPA} onChange={e => set("overrideAvgLPA", e.target.value)}
                        placeholder="e.g. 14.5" />
                    </div>
                  </div>
                  <div className="asg-field">
                    <label>Override Max LPA</label>
                    <div className="asg-input-wrap">
                      <span>₹</span>
                      <input type="number" className="asg-input" step="0.1" min="0"
                        value={form.overrideMaxLPA} onChange={e => set("overrideMaxLPA", e.target.value)}
                        placeholder="e.g. 25.0" />
                    </div>
                  </div>
                </div>
                <div className="asg-field">
                  <label>Reason for Override</label>
                  <input type="text" className="asg-input" value={form.overrideNote}
                    onChange={e => set("overrideNote", e.target.value)}
                    placeholder="e.g. Adjusted based on industry survey data 2024" />
                </div>
              </>
            )}
          </div>

          {/* ─ Salary by Experience ─ */}
          <div className="asg-section">
            <div className="asg-section__title">📈 Salary by Experience (LPA)</div>
            <div className="asg-form-row asg-form-row--5">
              {[
                ["expFresher", "Fresher (0-1yr)"],
                ["expJunior",  "Junior (1-3yr)"],
                ["expMid",     "Mid (3-5yr)"],
                ["expSenior",  "Senior (5-8yr)"],
                ["expLead",    "Lead (8+yr)"],
              ].map(([key, label]) => (
                <div key={key} className="asg-field">
                  <label>{label}</label>
                  <div className="asg-input-wrap">
                    <span>₹</span>
                    <input type="number" className="asg-input" step="0.5" min="0"
                      value={form[key]} onChange={e => set(key, e.target.value)}
                      placeholder="LPA" />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* ─ Salary by City ─ */}
          <div className="asg-section">
            <div className="asg-section__title">🏙️ Salary by City (Avg LPA)</div>
            <div className="asg-form-row asg-form-row--5">
              {[
                ["cityBangalore","Bangalore"],
                ["cityMumbai",   "Mumbai"],
                ["cityDelhi",    "Delhi"],
                ["cityHyderabad","Hyderabad"],
                ["cityPune",     "Pune"],
              ].map(([key, label]) => (
                <div key={key} className="asg-field">
                  <label>{label}</label>
                  <div className="asg-input-wrap">
                    <span>₹</span>
                    <input type="number" className="asg-input" step="0.5" min="0"
                      value={form[key]} onChange={e => set(key, e.target.value)}
                      placeholder="LPA" />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {err && <div className="asg-error">{err}</div>}
        </div>

        <div className="asg-edit-modal__footer">
          <button className="asg-btn asg-btn--ghost" onClick={onClose}>Cancel</button>
          <button className="asg-btn asg-btn--primary" onClick={handleSave} disabled={saving}>
            {saving ? "Saving…" : "💾 Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────
   ADD ROLE MODAL
───────────────────────────────────────── */
function AddRoleModal({ onAdd, onClose }) {
  const [role, setRole]     = useState("");
  const [icon, setIcon]     = useState("💼");
  const [cat, setCat]       = useState("Technology");
  const [desc, setDesc]     = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr]       = useState("");

  const handleAdd = async () => {
    if (!role.trim()) { setErr("Role name is required"); return; }
    setSaving(true); setErr("");
    try {
      const res  = await fetch(`${API_BASE}/salary-guide/admin/role`, {
        method: "POST", headers: adminHeaders,
        body: JSON.stringify({ role: role.trim(), icon, category: cat, description: desc }),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error);
      onAdd(json.data);
    } catch (e) {
      setErr(e.message || "Failed to add role");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="asg-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="asg-add-modal">
        <div className="asg-edit-modal__header">
          <div className="asg-edit-modal__title">➕ Add New Role</div>
          <button className="asg-icon-btn" onClick={onClose}>✕</button>
        </div>
        <div className="asg-edit-modal__body">
          <div className="asg-field" style={{ marginBottom:14 }}>
            <label>Role Name <span className="asg-required">*</span></label>
            <input type="text" className="asg-input" value={role}
              onChange={e => setRole(e.target.value)} placeholder="e.g. Machine Learning Engineer" />
          </div>
          <div className="asg-form-row" style={{ marginBottom:14 }}>
            <div className="asg-field">
              <label>Icon</label>
              <div className="asg-icon-picker">
                {ICONS.map(ic => (
                  <button key={ic}
                    className={`asg-icon-opt ${icon === ic ? "asg-icon-opt--active" : ""}`}
                    onClick={() => setIcon(ic)}>{ic}</button>
                ))}
              </div>
            </div>
            <div className="asg-field">
              <label>Category</label>
              <select className="asg-select" value={cat} onChange={e => setCat(e.target.value)}>
                {CATEGORIES.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <div className="asg-field">
            <label>Description</label>
            <textarea className="asg-textarea" rows={2} value={desc}
              onChange={e => setDesc(e.target.value)} placeholder="Short description…" />
          </div>
          {err && <div className="asg-error">{err}</div>}
        </div>
        <div className="asg-edit-modal__footer">
          <button className="asg-btn asg-btn--ghost" onClick={onClose}>Cancel</button>
          <button className="asg-btn asg-btn--primary" onClick={handleAdd} disabled={saving}>
            {saving ? "Adding…" : "➕ Add Role"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────
   MAIN ADMIN PAGE
───────────────────────────────────────── */
export default function AdminSalaryGuide() {
  const [entries, setEntries]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [search, setSearch]       = useState("");
  const [catFilter, setCatFilter] = useState("all");
  const [editEntry, setEditEntry] = useState(null);
  const [showAdd, setShowAdd]     = useState(false);
  const [recalcLoading, setRecalcLoading] = useState(false);
  const [recalcResult, setRecalcResult]   = useState(null);
  const [toast, setToast]         = useState(null);

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const fetchAll = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res  = await fetch(`${API_BASE}/salary-guide/admin/all`, { headers: adminHeaders });
      const json = await res.json();
      if (!json.success) throw new Error(json.error);
      setEntries(json.data);
    } catch (e) {
      setError(e.message || "Failed to load data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleSave = (updated) => {
    setEntries(prev => prev.map(e => e._id === updated._id ? updated : e));
    setEditEntry(null);
    showToast(`✅ "${updated.role}" updated successfully`);
  };

  const handleAdd = (newEntry) => {
    setEntries(prev => [newEntry, ...prev]);
    setShowAdd(false);
    showToast(`✅ "${newEntry.role}" added to salary guide`);
  };

  const handleDelete = async (entry) => {
    if (!window.confirm(`Delete "${entry.role}" from salary guide?`)) return;
    try {
      const res  = await fetch(`${API_BASE}/salary-guide/admin/${entry._id}`, {
        method: "DELETE", headers: adminHeaders,
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error);
      setEntries(prev => prev.filter(e => e._id !== entry._id));
      showToast(`🗑️ "${entry.role}" deleted`);
    } catch (e) {
      showToast(`❌ Delete failed: ${e.message}`, "error");
    }
  };

  const handleRecalc = async () => {
    setRecalcLoading(true); setRecalcResult(null);
    try {
      const res  = await fetch(`${API_BASE}/salary-guide/admin/recalc`, {
        method: "POST", headers: adminHeaders,
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error);
      setRecalcResult(json);
      await fetchAll();
      showToast(`🔄 Recalculated ${json.recalculated} roles from job postings`);
    } catch (e) {
      showToast(`❌ Recalc failed: ${e.message}`, "error");
    } finally {
      setRecalcLoading(false);
    }
  };

  const toggleOverride = async (entry) => {
    try {
      const res  = await fetch(`${API_BASE}/salary-guide/admin/${entry._id}`, {
        method: "PUT", headers: adminHeaders,
        body: JSON.stringify({ adminOverride: !entry.adminOverride }),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error);
      setEntries(prev => prev.map(e => e._id === entry._id ? json.data : e));
      showToast(`Override ${json.data.adminOverride ? "enabled" : "disabled"} for "${entry.role}"`);
    } catch (e) {
      showToast(`❌ ${e.message}`, "error");
    }
  };

  const categories = ["all", ...new Set(entries.map(e => e.category).filter(Boolean))];
  const filtered = entries.filter(e => {
    const matchCat = catFilter === "all" || e.category === catFilter;
    const matchSrc = !search || e.role.toLowerCase().includes(search.toLowerCase());
    return matchCat && matchSrc;
  });

  const overrideCount = entries.filter(e => e.adminOverride).length;
  const totalJobs     = entries.reduce((a, e) => a + (e.jobCount || 0), 0);

  return (
    <div className="asg-root">

      {/* Toast */}
      {toast && (
        <div className={`asg-toast asg-toast--${toast.type}`}>{toast.msg}</div>
      )}

      {/* ── Header ── */}
      <div className="asg-page-header">
        <div>
          <h1 className="asg-page-title">💰 Salary Guide Manager</h1>
          <p className="asg-page-sub">
            Manage salary data displayed to users. Toggle admin overrides to customize any role.
          </p>
        </div>
        <div className="asg-header-actions">
          <button className="asg-btn asg-btn--recalc" onClick={handleRecalc} disabled={recalcLoading}>
            {recalcLoading ? "⏳ Recalculating…" : "🔄 Recalc from Jobs"}
          </button>
          <button className="asg-btn asg-btn--primary" onClick={() => setShowAdd(true)}>
            ➕ Add Role
          </button>
        </div>
      </div>

      {/* ── Stats summary ── */}
      <div className="asg-stats-row">
        {[
          { icon:"📋", val:entries.length,    lbl:"Total Roles"      },
          { icon:"✏️", val:overrideCount,      lbl:"Admin Overrides"  },
          { icon:"💼", val:totalJobs,          lbl:"Job Postings Used" },
          { icon:"📊", val:entries.filter(e => e.avgSalaryLPA > 0).length, lbl:"Roles with Data" },
        ].map((s, i) => (
          <div key={i} className="asg-stat-card">
            <span className="asg-stat-card__icon">{s.icon}</span>
            <div className="asg-stat-card__val">{s.val}</div>
            <div className="asg-stat-card__lbl">{s.lbl}</div>
          </div>
        ))}
      </div>

      {/* Recalc result */}
      {recalcResult && (
        <div className="asg-recalc-banner">
          ✅ Recalculated <strong>{recalcResult.recalculated}</strong> roles from live job postings.
          <button className="asg-recalc-banner__close" onClick={() => setRecalcResult(null)}>✕</button>
        </div>
      )}

      {/* ── Filters ── */}
      <div className="asg-filters">
        <div className="asg-search">
          <span>🔍</span>
          <input type="text" placeholder="Search roles…" value={search}
            onChange={e => setSearch(e.target.value)} />
        </div>
        <div className="asg-cat-tabs">
          {categories.map(cat => (
            <button key={cat}
              className={`asg-cat-tab ${catFilter === cat ? "asg-cat-tab--active" : ""}`}
              onClick={() => setCatFilter(cat)}>
              {cat === "all" ? "All" : cat}
            </button>
          ))}
        </div>
        <div className="asg-filter-count">{filtered.length} roles</div>
      </div>

      {/* ── Table ── */}
      {loading ? (
        <div className="asg-loading-center"><div className="asg-spinner" /><span>Loading…</span></div>
      ) : error ? (
        <div className="asg-error-box">
          <span>❌ {error}</span>
          <button className="asg-btn asg-btn--ghost asg-btn--sm" onClick={fetchAll}>Retry</button>
        </div>
      ) : (
        <div className="asg-table-wrap">
          <table className="asg-table">
            <thead>
              <tr>
                <th>Role</th>
                <th>Category</th>
                <th>Calculated Avg</th>
                <th>Override Avg</th>
                <th>Shown to Users</th>
                <th>Job Count</th>
                <th>Override</th>
                <th>Last Updated</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(entry => (
                <tr key={entry._id} className={entry.adminOverride ? "asg-row--overridden" : ""}>
                  <td>
                    <div className="asg-role-cell">
                      <span className="asg-role-icon">{entry.icon || "💼"}</span>
                      <div>
                        <div className="asg-role-name">{entry.role}</div>
                        <div className="asg-role-slug">{entry.roleSlug}</div>
                      </div>
                    </div>
                  </td>
                  <td><span className="asg-cat-badge">{entry.category || "—"}</span></td>
                  <td>
                    <span className="asg-salary-val asg-salary-val--calc">
                      {formatLPA(entry.avgSalaryLPA)}
                    </span>
                  </td>
                  <td>
                    {entry.adminOverride
                      ? <span className="asg-salary-val asg-salary-val--override">{formatLPA(entry.overrideAvgLPA)}</span>
                      : <span className="asg-na">—</span>}
                  </td>
                  <td>
                    <span className={`asg-shown-val ${entry.adminOverride ? "asg-shown-val--override" : "asg-shown-val--calc"}`}>
                      {entry.adminOverride
                        ? `${formatLPA(entry.overrideAvgLPA)} ✏️`
                        : `${formatLPA(entry.avgSalaryLPA)} 📊`}
                    </span>
                  </td>
                  <td>
                    <span className="asg-job-count">{entry.jobCount || 0} jobs</span>
                  </td>
                  <td>
                    <label className="asg-toggle">
                      <input type="checkbox" checked={!!entry.adminOverride}
                        onChange={() => toggleOverride(entry)} />
                      <span className="asg-toggle__slider" />
                    </label>
                  </td>
                  <td>
                    <span className="asg-date">
                      {entry.updatedAt ? new Date(entry.updatedAt).toLocaleDateString("en-IN", { day:"2-digit", month:"short", year:"numeric" }) : "—"}
                    </span>
                    {entry.updatedBy && (
                      <div className="asg-updated-by">by {entry.updatedBy}</div>
                    )}
                  </td>
                  <td>
                    <div className="asg-actions">
                      <button className="asg-action-btn asg-action-btn--edit"
                        onClick={() => setEditEntry(entry)} title="Edit">✏️</button>
                      <button className="asg-action-btn asg-action-btn--delete"
                        onClick={() => handleDelete(entry)} title="Delete">🗑️</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modals */}
      {editEntry && (
        <EditModal entry={editEntry} onSave={handleSave} onClose={() => setEditEntry(null)} />
      )}
      {showAdd && (
        <AddRoleModal onAdd={handleAdd} onClose={() => setShowAdd(false)} />
      )}
    </div>
  );
}
